import { useMutation, useQuery } from "@apollo/client";
import {
  BillingInfoInput,
  SubscriptionPlan,
  User,
  UserMetadataInput,
} from "@onmo/graphql/graphql";
import { meAAAGQL, updateUserMetadataGQL } from "../graphql";
import { useCallback, useRef, useState } from "react";
import { PageRoutes } from "../constants/routes";
import { getBillingRegion, hasBillingInfo } from "../models/billing";
import { useSubscriptionNavigate } from "./useSubscriptionNavigate";

type Step = "idle" | "confirm" | "billing";

export const useSubscriptionFlow = () => {
  const navigate = useSubscriptionNavigate();
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [step, setStep] = useState<Step>("idle");
  const isProcessingRef = useRef(false);

  const { data: meData } = useQuery<{ me: User }>(meAAAGQL);
  const isBillingSaved = hasBillingInfo(meData?.me?.metadata?.billingInfo);

  const [updateUserMetadata] = useMutation<
    { updateUserMetadata: { message: string } },
    { input: UserMetadataInput }
  >(updateUserMetadataGQL);

  const close = useCallback(() => {
    setStep("idle");
    setPlan(null);
    isProcessingRef.current = false;
  }, []);

  const start = useCallback((selectedPlan?: SubscriptionPlan | null) => {
    if (!selectedPlan) return;
    isProcessingRef.current = false;
    setPlan(selectedPlan);
    setStep("confirm");
  }, []);

  const goToPayment = useCallback(
    (planId: string) => {
      navigate(`${PageRoutes.Payment}?planId=${planId}`);
    },
    [navigate],
  );

  const confirm = useCallback(() => {
    if (!plan?.id || isProcessingRef.current) return;
    isProcessingRef.current = true;
    if (isBillingSaved) {
      goToPayment(plan.id);
    } else {
      isProcessingRef.current = false;
      setStep("billing");
    }
  }, [plan?.id, isBillingSaved, goToPayment]);

  const skipBilling = useCallback(() => {
    if (plan?.id) goToPayment(plan.id);
  }, [plan?.id, goToPayment]);

  const submitBilling = useCallback(
    async (data: BillingInfoInput) => {
      if (!plan?.id) return;
      await updateUserMetadata({
        variables: { input: { billingInfo: data } },
        update: (cache) => {
          const existing = cache.readQuery<{ me: User }>({ query: meAAAGQL });
          if (!existing?.me) return;
          cache.writeQuery({
            query: meAAAGQL,
            data: {
              me: {
                ...existing.me,
                metadata: {
                  ...existing.me.metadata,
                  billingInfo: {
                    __typename: "BillingInfo",
                    taxId: null,
                    address: null,
                    postalCode: null,
                    city: null,
                    ...data,
                  },
                },
              },
            },
          });
        },
      });
      goToPayment(plan.id);
    },
    [plan?.id, updateUserMetadata, goToPayment],
  );

  return {
    start,
    close,
    confirm,
    submitBilling,
    skipBilling,
    plan,
    billingRegion: getBillingRegion(plan),
    isConfirmOpen: step === "confirm",
    isBillingOpen: step === "billing",
  };
};
